/**
 * Site Content admin tab. Lists every curated config key and renders an
 * editor appropriate to its kind (text/textarea/image/color). Each row
 * saves independently to /api/site-config/<key> when its Save button
 * is clicked.
 *
 * Image fields use the generic /api/uploads/image endpoint and store
 * the returned URL into the config value.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Card, Flex, Heading, Text, Button, TextField, TextArea } from '@radix-ui/themes';
import api from '../../utils/api';
import { SITE_CONFIG_FIELDS, type ConfigField } from '../../utils/siteConfig';

interface RowProps {
  field: ConfigField;
  initialValue: string;
  // Read-only row, e.g. a gated branding field on a plan without the feature.
  disabled?: boolean;
  // Called after a save or a clear succeeds.
  onSaved?: () => void;
}

// One editable config key. The Branding screen reuses this row.
export const SiteConfigRow: React.FC<RowProps> = ({ field, initialValue, disabled = false, onSaved }) => {
  const [value, setValue] = useState(initialValue);
  const [savedValue, setSavedValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const dirty = value !== savedValue;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/site-config/${encodeURIComponent(field.key)}`, { value });
      setSavedValue(value);
      onSaved?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/site-config/${encodeURIComponent(field.key)}`);
      setValue('');
      setSavedValue('');
      onSaved?.();
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || 'Clear failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/api/uploads/image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (url) setValue(url);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card size="2">
      {/* A disabled fieldset disables every input and button inside. */}
      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <Flex direction="column" gap="2">
        <Box>
          <Text as="div" weight="medium">{field.label}</Text>
          {field.description && (
            <Text as="div" size="1" color="gray">{field.description}</Text>
          )}
          <Text as="div" size="1" color="gray" style={{ fontFamily: 'monospace', marginTop: 2 }}>
            {field.key}
          </Text>
        </Box>

        {field.kind === 'textarea' && (
          <TextArea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            placeholder="(empty)"
          />
        )}

        {field.kind === 'text' && (
          <TextField.Root
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="(empty)"
          />
        )}

        {field.kind === 'color' && (
          <Flex gap="2" align="center">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => setValue(e.target.value)}
              style={{ width: 48, height: 36, padding: 0, border: '1px solid #333', borderRadius: 4, background: 'transparent' }}
            />
            <TextField.Root
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="#000000"
              style={{ flex: 1 }}
            />
          </Flex>
        )}

        {field.kind === 'image' && (
          <Flex gap="3" align="center">
            {value ? (
              <img
                src={value}
                alt=""
                style={{
                  width: 96, height: 96, objectFit: 'cover',
                  borderRadius: 6, border: '1px solid var(--gray-5)',
                  background: 'var(--gray-2)',
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
              />
            ) : (
              <Box style={{
                width: 96, height: 96, borderRadius: 6,
                background: 'var(--gray-3)', border: '1px dashed var(--gray-6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Text size="1" color="gray">no image</Text>
              </Box>
            )}
            <Flex direction="column" gap="2" flexGrow="1">
              <TextField.Root
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="https://..."
              />
              <Flex gap="2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="soft"
                  size="2"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload image'}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}

        {error && <Text size="1" color="red">{error}</Text>}

        <Flex gap="2" justify="end">
          <Button variant="ghost" onClick={clear} disabled={saving || !savedValue}>
            Clear
          </Button>
          <Button onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
          </Button>
        </Flex>
      </Flex>
      </fieldset>
    </Card>
  );
};

const SiteContentManager: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/api/site-config/mine')
      .then((res) => setValues(res.data?.data ?? {}))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return <Box p="4"><Text color="gray">Loading…</Text></Box>;
  }

  return (
    <Box p="4">
      <Heading size="5" mb="2">Site Content</Heading>
      <Text as="p" color="gray" size="2" mb="4" style={{ maxWidth: 720 }}>
        Edit the copy and images shown on the public-facing pages (Home, About,
        Contact). Each row saves independently. Clearing a value falls back to
        whatever the code has hardcoded as a default. Brand name, logo, color,
        and links are in Settings → Branding.
      </Text>
      <Flex direction="column" gap="3" maxWidth="780px">
        {SITE_CONFIG_FIELDS.map((field) => (
          <SiteConfigRow key={field.key} field={field} initialValue={values[field.key] ?? ''} />
        ))}
      </Flex>
    </Box>
  );
};

export default SiteContentManager;
